import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  chatMessagesQueryOptions,
  isChatClosed,
  isChatThreadSettled,
  promptFor,
  useChatThread,
  useSendChatMessage,
} from '#/api/chats'
import type { ChatMessage, ChatSendOrigin } from '#/api/chats'
import { hasPermission, useCan } from '#/user/me'
import { toChatRunOptions, useChatOptions } from '#/hooks/use-chat-options'
import { RouteError } from '#/components/route-error'
import { RoutePending } from '#/components/route-pending'
import { EvidenceViewer } from '#/components/evidence-viewer/evidence-viewer'
import { ChatComposer } from '#/components/ask-annwn/chat-composer'
import { ChatThread, isReplyShown } from '#/components/ask-annwn/chat-thread'
import {
  FINDING_MARK,
  findingIdOf,
  findFindingMark,
  provenanceTarget,
} from '#/components/ask-annwn/evidence/finding-target'
import { EvidenceColumn } from '#/components/ask-annwn/evidence/evidence-column'
import { ConversationFrame } from '#/components/ask-annwn/reply-column'
import { useFoldThreadPanelWhile } from '#/components/ask-annwn/thread-panel-fold'
import { findingsByRecord } from '#/components/ask-annwn/evidence/file-sources'
import { recordValuesOf } from '#/components/ask-annwn/evidence/worked-figures'
import type { EvidenceTab } from '#/components/ask-annwn/reply-trust'

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/ask-annwn/$chatId',
)({
  // Gated on the same permission the page requires: an unconditional prefetch
  // would 403 and crash the route before the component's `useCan` ever runs.
  loader: ({ context, params }) => {
    if (!hasPermission(context.me, 'annwn:chats:read')) return
    return context.queryClient.ensureQueryData(
      chatMessagesQueryOptions(params.accountId, params.chatId),
    )
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: ChatPage,
})

function ChatPage() {
  const { t } = useTranslation()
  const { accountId, chatId } = Route.useParams()
  const canRead = useCan('annwn:chats:read')

  if (!canRead) {
    return (
      <p className="p-8 text-sm text-muted-foreground">
        {t('askAnnwn.noAccess')}
      </p>
    )
  }

  // Remount on chat change so the evidence column and viewer don't carry
  // over between conversations.
  return <Conversation key={chatId} accountId={accountId} chatId={chatId} />
}

function Conversation({
  accountId,
  chatId,
}: {
  accountId: string
  chatId: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canCreate = useCan('annwn:chats:create')
  const canConfigure = useCan('annwn:chats:configure')
  const canViewInternal = useCan('annwn:internal:read')
  const canReadDocuments = useCan('annwn:documents:read')
  // One instance, shared with the composer's gear: see ChatComposer.options.
  const { options, update } = useChatOptions()

  const {
    data: messages,
    refetch,
    isError: pollFailed,
  } = useChatThread(accountId, chatId)

  // What the evidence column is showing, if anything: one reply, at the whole
  // answer's scope or narrowed to one of its figures or claims, on one tab.
  const [panel, setPanel] = useState<EvidencePanel | null>(null)
  // The evidence viewer, if open: the scope it opened from, and where it is
  // deep-linked to (a source record from a reply's sources, or a document from
  // the scope's own evidence).
  const [viewer, setViewer] = useState<{
    scope: string | null
    focus: EvidenceFocus
  } | null>(null)
  // What opened the column, so closing it puts the reader back there: a trust
  // card, or a mark in the prose or a chart, whose SVG marks aren't HTML.
  const opener = useRef<HTMLElement | SVGElement | null>(null)
  const openPanel = (next: EvidencePanel) => {
    const focused = document.activeElement
    if (focused instanceof HTMLElement || focused instanceof SVGElement)
      opener.current = focused
    setPanel(next)
    setViewer(null)
  }
  const shownReply = panel
    ? messages.find(
        (m) =>
          m.message_uuid === panel.messageUuid && isReplyShown(m, messages),
      )
    : undefined
  // A figure the thread no longer holds narrows nothing; a reply it no longer
  // shows opens nothing.
  const scope =
    panel?.scope &&
    shownReply &&
    provenanceTarget(shownReply.findings ?? [], panel.scope)
      ? panel.scope
      : null
  const shownPanel = panel && shownReply ? { ...panel, scope } : null
  useFoldThreadPanelWhile(shownPanel !== null)
  // A viewer shows only over the scope it opened from, so a figure that has
  // left the reply takes its evidence with it.
  const viewerShown =
    shownPanel !== null &&
    shownReply !== undefined &&
    viewer !== null &&
    viewer.scope === shownPanel.scope
  const openViewer = (focus: EvidenceFocus) => {
    if (shownPanel) setViewer({ scope: shownPanel.scope, focus })
  }
  // Back to the figure the column was narrowed to, on the mark that opened it
  // where that mark opens it (a value can be marked in the prose and in a
  // chart alike, and some browsers leave focus where it was on a click); else
  // whatever opened it.
  const closePanel = () => {
    setPanel(null)
    setViewer(null)
    const from = opener.current
    if (!shownPanel?.scope) {
      from?.focus()
      return
    }
    const mark = from?.closest<HTMLElement | SVGElement>(FINDING_MARK)
    const opened =
      mark?.isConnected && findingIdOf(mark) === shownPanel.scope ? mark : null
    const target =
      opened ??
      findFindingMark(shownPanel.messageUuid, shownPanel.scope) ??
      from
    target?.focus()
  }
  // Steps the column back to the whole answer, on the tab the reader last had
  // open there.
  const toAnswer = () => {
    if (!panel) return
    setPanel({ ...panel, scope: null, tab: panel.answerTab })
    setViewer(null)
  }

  // Escape steps back one scope, then closes — unless something above the
  // column (the evidence viewer, a menu) or a text field has it.
  useEffect(() => {
    if (!shownPanel || viewerShown) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      )
        return
      event.preventDefault()
      if (shownPanel.scope) toAnswer()
      else closePanel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  // So the clarification widget's "Something else" can hand the turn to the box.
  const composerRef = useRef<HTMLTextAreaElement>(null)

  // Put the cursor in the box on arrival: opening a conversation is a prompt to
  // type, and every chat app behaves this way. Runs once per conversation —
  // `Conversation` is keyed on chatId, so switching chats remounts it — and does
  // nothing when there's no composer to focus (no create permission).
  useEffect(() => {
    composerRef.current?.focus()
  }, [])
  const send = useSendChatMessage(accountId, chatId)

  const busy = !isChatThreadSettled(messages) || send.isPending

  // Gated on the thread being settled as well as the send being in flight:
  // `onSuccess` writes the real pair before the mutation reports done, so
  // without it the message would show twice for that moment.
  const pendingMessage =
    send.isPending && isChatThreadSettled(messages)
      ? send.variables.content
      : null

  // Resolves false on failure so the composer keeps the text; the error itself
  // is surfaced from the mutation's own state.
  const ask = async (content: string, via: ChatSendOrigin = 'composer') => {
    try {
      await send.mutateAsync({
        content,
        via,
        options: toChatRunOptions(options, canConfigure),
      })
      return true
    } catch {
      return false
    }
  }

  // "Try again" re-asks the question the failed run was answering, which appends
  // a fresh exchange rather than mutating the failed one — the BFF has no
  // re-run endpoint, and the failed reply stays in the thread as a record.
  const retry = (reply: ChatMessage) => {
    const prompt = promptFor(messages, reply)
    if (prompt) void ask(prompt, 'retry')
  }

  const threadView = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <ChatThread
        accountUuid={accountId}
        chatUuid={chatId}
        messages={messages}
        canViewInternal={canViewInternal}
        activeTrust={
          shownPanel && !shownPanel.scope
            ? { messageUuid: shownPanel.messageUuid, tab: shownPanel.tab }
            : null
        }
        onOpenTrust={(messageUuid, tab) =>
          openPanel({ messageUuid, scope: null, tab, answerTab: tab })
        }
        // A figure or claim narrows the column to it, on its working, in
        // place where the column is already open on its reply. The one it is
        // narrowed to lets go again, back to the whole answer, as Esc does.
        onOpenFinding={(messageUuid, findingId) => {
          if (
            shownPanel?.messageUuid === messageUuid &&
            shownPanel.scope === findingId
          )
            toAnswer()
          else
            openPanel({
              messageUuid,
              scope: findingId,
              tab: 'working',
              answerTab:
                panel?.messageUuid === messageUuid
                  ? panel.answerTab
                  : 'working',
            })
        }}
        activeFinding={
          shownPanel?.scope
            ? {
                messageUuid: shownPanel.messageUuid,
                findingId: shownPanel.scope,
              }
            : null
        }
        onRetry={canCreate ? retry : undefined}
        busy={busy}
        // Answering a clarifying question is an ordinary send, so it needs
        // the same permission as typing one; without it the question stays
        // as a record rather than offering controls that would 403.
        onAnswerClarification={
          canCreate ? (option) => void ask(option, 'clarification') : undefined
        }
        // A suggested follow-up is an ordinary send too, under the same
        // permission.
        onAskNextStep={
          canCreate ? (step) => void ask(step, 'next_step') : undefined
        }
        onFocusComposer={() => composerRef.current?.focus()}
        pendingMessage={pendingMessage}
        pollFailed={pollFailed}
        onCheckAgain={() => void refetch()}
        onStartNewChat={() =>
          void navigate({
            to: '/accounts/$accountId/ask-annwn',
            params: { accountId },
          })
        }
      />
      {canCreate ? (
        <div className="flex shrink-0 justify-center px-8 py-6">
          <ChatComposer
            ref={composerRef}
            busy={busy}
            closed={isChatClosed(messages)}
            canConfigure={canConfigure}
            options={options}
            onOptionsChange={update}
            error={send.isError ? t('askAnnwn.composer.sendFailed') : null}
            onSubmit={ask}
          />
        </div>
      ) : null}
    </div>
  )

  const evidence =
    shownPanel && shownReply ? (
      <EvidenceColumn
        // Remounted per reply, so its column starts at the top; a figure
        // of the same reply narrows it in place.
        key={shownPanel.messageUuid}
        accountUuid={accountId}
        chatUuid={chatId}
        message={shownReply}
        scope={shownPanel.scope}
        tab={shownPanel.tab}
        onTabChange={(tab) =>
          setPanel({
            ...shownPanel,
            tab,
            answerTab: shownPanel.scope ? shownPanel.answerTab : tab,
          })
        }
        onScopeChange={(next) => {
          if (next === null) toAnswer()
          else setPanel({ ...shownPanel, scope: next })
        }}
        onClose={closePanel}
        onViewEvidence={(focusNodeUuid) => openViewer({ focusNodeUuid })}
        onViewDocument={(focusFileUploadUuid) =>
          openViewer({ focusFileUploadUuid })
        }
        canDownload={canReadDocuments}
      />
    ) : null

  return (
    <ConversationFrame>
      {threadView}
      {evidence}

      {/* The scope's own evidence: the whole reply's, or one figure's,
          leading back to its column. */}
      {viewerShown ? (
        <EvidenceViewer
          accountUuid={accountId}
          source={
            shownPanel.scope
              ? {
                  kind: 'chat_finding',
                  chatUuid: chatId,
                  messageUuid: shownPanel.messageUuid,
                  findingId: shownPanel.scope,
                }
              : {
                  kind: 'chat_message',
                  chatUuid: chatId,
                  messageUuid: shownPanel.messageUuid,
                }
          }
          {...viewer.focus}
          title={
            shownPanel.scope
              ? provenanceTarget(shownReply.findings ?? [], shownPanel.scope)
                  ?.finding.label
              : undefined
          }
          question={askedBefore(messages, shownReply.message_uuid)}
          open
          onOpenChange={(open) => {
            if (!open) setViewer(null)
          }}
          onBack={shownPanel.scope ? () => setViewer(null) : undefined}
          // At the whole answer, each record says which findings it went
          // into; a figure's own evidence is all one finding's.
          fedInto={shownPanel.scope ? undefined : fedIntoOf(shownReply)}
          recordValues={recordValuesOf(shownReply)}
          canReadDocuments={canReadDocuments}
        />
      ) : null}
    </ConversationFrame>
  )
}

// Each record of a reply, by uuid, with the labels of the findings it went
// into.
function fedIntoOf(reply: ChatMessage): ReadonlyMap<string, Array<string>> {
  return new Map(
    [...findingsByRecord(reply).records].map(([uuid, findings]) => [
      uuid,
      findings.map((f) => f.label),
    ]),
  )
}

// The question a reply answers: the reader's message just before it.
function askedBefore(
  messages: ReadonlyArray<ChatMessage>,
  replyUuid: string,
): string | undefined {
  const at = messages.findIndex((m) => m.message_uuid === replyUuid)
  const asked = messages
    .slice(0, Math.max(at, 0))
    .filter((m) => m.role === 'user')
    .at(-1)
  return asked?.content ?? undefined
}

// The evidence column's state: the reply, the figure or claim it is narrowed
// to (null for the whole answer), the tab, and the tab last open at the
// answer's scope, which stepping back out returns to.
type EvidencePanel = {
  messageUuid: string
  scope: string | null
  tab: EvidenceTab
  answerTab: EvidenceTab
}

type EvidenceFocus = { focusNodeUuid: string } | { focusFileUploadUuid: string }
